// Formuläret för en tjänst i kontot. Talen hålls som text medan byrån skriver,
// och blir en tjänst i katalogens form först när den sparas. Varje
// prissättningssätt har sina egna fält, så att inget försvinner om byrån
// byter sätt och sedan tillbaka.
import { lasHeltal, lasKronor, lasTal, skrivKronor, skrivTal } from './belopp.js';
import {
  BOLAGSFORMER,
  type Billing,
  type LegalForm,
  type Pricing,
  type PricingType,
  type Service,
} from './texter.js';

export type Tidsnorm =
  { slag: 'drivare'; drivare: string; minuter: string } | { slag: 'fast'; minuter: string };

export type Visning = 'hoursAndRate' | 'amountOnly' | 'monthlyCap';

export interface TjanstForm {
  /** null för en ny tjänst. Id:t skapas från namnet när den sparas. */
  id: string | null;
  label: string;
  /** Det som ingår, en rad per punkt. */
  includes: string;
  billing: Billing;
  /** Tom betyder alla bolagsformer. */
  appliesTo: LegalForm[];
  /** Bolagsformer där tjänsten alltid tas med i offerten (ADR 0101). */
  alltidMed: LegalForm[];
  typ: PricingType;
  fast: { pris: string };
  perUnit: { enhet: string; pris: string };
  trappa: { drivare: string; steg: { upTo: string; pris: string }[] };
  bolagsform: Record<LegalForm, string>;
  timpris: {
    pris: string;
    normer: Tidsnorm[];
    minTimmar: string;
    avrunda: string;
    visning: Visning;
  };
}

export const tomForm = (billing: Billing = 'monthly'): TjanstForm => ({
  id: null,
  label: '',
  includes: '',
  billing,
  appliesTo: [],
  alltidMed: [],
  typ: 'fixed',
  fast: { pris: '' },
  perUnit: { enhet: 'anställd', pris: '' },
  trappa: { drivare: 'transactionsPerMonth', steg: [{ upTo: '', pris: '' }] },
  bolagsform: { AB: '', EF: '', HB: '', KB: '', EK: '', BRF: '', IF: '' },
  timpris: {
    pris: '',
    normer: [{ slag: 'drivare', drivare: 'transactionsPerMonth', minuter: '' }],
    minTimmar: '0',
    avrunda: '0.5',
    visning: 'hoursAndRate',
  },
});

export function formFran(s: Service): TjanstForm {
  const f = tomForm(s.billing);
  f.id = s.id;
  f.label = s.label;
  f.includes = (s.includes ?? []).join('\n');
  f.appliesTo = s.appliesTo ? [...s.appliesTo] : [];
  f.alltidMed = s.requiredFor ? [...s.requiredFor] : [];
  const p = s.pricing;
  f.typ = p.type;
  switch (p.type) {
    case 'fixed':
      f.fast = { pris: skrivKronor(p.price) };
      break;
    case 'perUnit':
      f.perUnit = { enhet: p.unit, pris: skrivKronor(p.price) };
      break;
    case 'tiered':
      f.trappa = {
        drivare: p.driver,
        steg: p.tiers.map((t) => ({ upTo: String(t.upTo), pris: skrivKronor(t.price) })),
      };
      break;
    case 'byLegalForm':
      for (const { kod } of BOLAGSFORMER) {
        const pris = p.prices[kod];
        f.bolagsform[kod] = pris === undefined ? '' : skrivKronor(pris);
      }
      break;
    case 'hourly':
      f.timpris = {
        pris: skrivKronor(p.rate),
        normer: p.timeNorms.map((n) =>
          'driver' in n
            ? { slag: 'drivare', drivare: n.driver, minuter: skrivTal(n.minutesPerUnit) }
            : { slag: 'fast', minuter: String(n.fixedMinutes) },
        ),
        minTimmar: skrivTal(p.minHours),
        avrunda: String(p.roundToHours),
        visning: p.display,
      };
      break;
  }
  return f;
}

/** Felen per fält. Nyckeln är fältets namn, till exempel "trappa.steg.1.pris". */
export type Fel = Record<string, string>;

const KRONOR = 'Skriv ett belopp i kronor, till exempel 1 500';

function pris(text: string, falt: string, fel: Fel): number {
  const ore = lasKronor(text);
  if (ore === null) fel[falt] = KRONOR;
  return ore ?? 0;
}

function prissattning(f: TjanstForm, fel: Fel): Pricing {
  switch (f.typ) {
    case 'fixed':
      return { type: 'fixed', price: pris(f.fast.pris, 'fast.pris', fel) };
    case 'perUnit': {
      const unit = f.perUnit.enhet.trim();
      if (!unit) fel['perUnit.enhet'] = 'Skriv enheten, till exempel anställd';
      return { type: 'perUnit', unit, price: pris(f.perUnit.pris, 'perUnit.pris', fel) };
    }
    case 'tiered': {
      if (f.trappa.steg.length === 0) fel['trappa.steg'] = 'Lägg till minst ett steg';
      let forra = 0;
      const tiers = f.trappa.steg.map((s, i) => {
        const upTo = lasHeltal(s.upTo);
        if (upTo === null || upTo < 1) fel[`trappa.steg.${i}.upTo`] = 'Skriv ett heltal, minst 1';
        else if (upTo <= forra) {
          fel[`trappa.steg.${i}.upTo`] = `Måste vara större än steget ovanför (${forra})`;
        }
        forra = Math.max(forra, upTo ?? 0);
        return { upTo: upTo ?? 0, price: pris(s.pris, `trappa.steg.${i}.pris`, fel) };
      });
      return { type: 'tiered', driver: f.trappa.drivare, tiers };
    }
    case 'byLegalForm': {
      const prices: Partial<Record<LegalForm, number>> = {};
      for (const { kod } of BOLAGSFORMER) {
        const text = f.bolagsform[kod].trim();
        if (text) prices[kod] = pris(text, `bolagsform.${kod}`, fel);
      }
      if (Object.keys(prices).length === 0) {
        fel.bolagsform = 'Ange pris för minst en bolagsform';
      }
      return { type: 'byLegalForm', prices };
    }
    case 'hourly': {
      const t = f.timpris;
      if (t.normer.length === 0) fel['timpris.normer'] = 'Lägg till minst en tidsnorm';
      const timeNorms = t.normer.map((n, i) => {
        const minuter = lasTal(n.minuter);
        if (minuter === null || minuter <= 0) {
          fel[`timpris.normer.${i}`] = 'Skriv antal minuter, mer än 0';
        }
        return n.slag === 'drivare'
          ? { driver: n.drivare, minutesPerUnit: minuter ?? 0 }
          : { fixedMinutes: Math.round(minuter ?? 0) };
      });
      const minHours = lasTal(t.minTimmar);
      if (minHours === null) fel['timpris.minTimmar'] = 'Skriv antal timmar, till exempel 2';
      return {
        type: 'hourly',
        rate: pris(t.pris, 'timpris.pris', fel),
        timeNorms,
        minHours: minHours ?? 0,
        roundToHours: Number(t.avrunda),
        display: t.visning,
      };
    }
  }
}

/** Om priset går att räkna utan mängder, så att tjänsten kan vara med alltid. Som i domain. */
export function kanAlltidMed(f: Pick<TjanstForm, 'typ' | 'timpris'>): boolean {
  if (f.typ === 'fixed' || f.typ === 'byLegalForm') return true;
  return f.typ === 'hourly' && f.timpris.normer.every((n) => n.slag === 'fast');
}

/**
 * Bolagsformerna där tjänsten kan vara med alltid: den ska gälla dem och,
 * med pris per bolagsform, ha ett pris för dem.
 */
export function mojligaAlltidMed(f: TjanstForm): LegalForm[] {
  if (!kanAlltidMed(f)) return [];
  return BOLAGSFORMER.map((b) => b.kod).filter(
    (kod) =>
      (f.appliesTo.length === 0 || f.appliesTo.includes(kod)) &&
      (f.typ !== 'byLegalForm' || f.bolagsform[kod].trim() !== ''),
  );
}

/** Ett id av namnet: "Löpande bokföring" blir "lopande-bokforing". Unikt i katalogen. */
export function nyttId(label: string, upptagna: string[]): string {
  const bas =
    label
      .toLowerCase()
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .replace(/^[^a-z]+/, '') || 'tjanst';
  let id = bas;
  for (let n = 2; upptagna.includes(id); n++) id = `${bas}-${n}`;
  return id;
}

/** Tjänsten i katalogens form, eller felen per fält. */
export function tillTjanst(
  f: TjanstForm,
  upptagnaId: string[],
): { tjanst: Service; fel?: undefined } | { fel: Fel; tjanst?: undefined } {
  const fel: Fel = {};
  const label = f.label.trim();
  if (!label) fel.label = 'Skriv vad tjänsten heter i offerten';
  const pricing = prissattning(f, fel);
  if (Object.keys(fel).length > 0) return { fel };
  const includes = f.includes
    .split('\n')
    .map((r) => r.trim())
    .filter(Boolean);
  const tjanst: Service = {
    id: f.id ?? nyttId(label, upptagnaId),
    label,
    billing: f.billing,
    pricing,
    ...(includes.length > 0 ? { includes } : {}),
    ...(f.appliesTo.length > 0 && f.appliesTo.length < BOLAGSFORMER.length
      ? { appliesTo: f.appliesTo }
      : {}),
  };
  // Bara de bolagsformer som fortfarande går, om byrån har ändrat priset efteråt.
  const mojliga = mojligaAlltidMed(f);
  const alltidMed = f.alltidMed.filter((kod) => mojliga.includes(kod));
  if (alltidMed.length > 0) tjanst.requiredFor = alltidMed;
  return { tjanst };
}
